import { useCallback, useEffect, useMemo, useState, type MouseEventHandler, type PointerEventHandler } from 'react';

import {
  resolveLineMeasurementBetween,
  type LineMeasurementPoint,
  type ResolvedLineMeasurement,
} from '../../../model/screencast-tooltips/screencastLineMeasurementResolver';
import type { ScreencastTooltips } from '../../../model/screencast-tooltips/types';

import type { GetImagePointFromClientPosition, TooltipScreenPosition } from './types';

type LineMeasurementInteraction = {
  pointerId: number;
  sourceTooltips: ScreencastTooltips;
  start: LineMeasurementPoint;
  current: LineMeasurementPoint;
  measurement: ResolvedLineMeasurement | null;
  cursorPosition: TooltipScreenPosition;
};

type UseLineMeasurementInteractionParams = {
  isEnabled: boolean;
  screencastTooltips?: ScreencastTooltips | null;
  getImagePointFromClientPosition: GetImagePointFromClientPosition;
  onStart?: VoidFunction;
};

type LineMeasurementStartEvent = {
  button: number;
  shiftKey: boolean;
};

type PreventableEvent = {
  preventDefault: VoidFunction;
  stopPropagation: VoidFunction;
};

export function useLineMeasurementInteraction({
  isEnabled,
  screencastTooltips,
  getImagePointFromClientPosition,
  onStart,
}: UseLineMeasurementInteractionParams) {
  const [lineMeasurement, setLineMeasurement] = useState<LineMeasurementInteraction | null>(null);

  useEffect(() => {
    const animationFrameId = requestAnimationFrame(() => {
      setLineMeasurement((prevLineMeasurement) => {
        if (!prevLineMeasurement) {
          return prevLineMeasurement;
        }

        if (!isEnabled || !screencastTooltips || prevLineMeasurement.sourceTooltips !== screencastTooltips) {
          return null;
        }

        return prevLineMeasurement;
      });
    });

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [isEnabled, screencastTooltips]);

  const activeLineMeasurement = useMemo(() => {
    if (!isEnabled || !screencastTooltips || lineMeasurement?.sourceTooltips !== screencastTooltips) {
      return null;
    }

    return lineMeasurement;
  }, [isEnabled, lineMeasurement, screencastTooltips]);

  const handleMouseDownCapture: MouseEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (shouldStartLineMeasurement(event) && isEnabled && screencastTooltips) {
        preventLineMeasurementEvent(event);
      }
    },
    [isEnabled, screencastTooltips],
  );

  const handlePointerDown: PointerEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (!shouldStartLineMeasurement(event) || !isEnabled || !screencastTooltips) {
        return;
      }

      const start = getImagePointFromClientPosition(event.currentTarget, event.clientX, event.clientY);
      if (!start) {
        return;
      }

      preventLineMeasurementEvent(event);
      event.currentTarget.setPointerCapture(event.pointerId);
      onStart?.();
      setLineMeasurement({
        pointerId: event.pointerId,
        sourceTooltips: screencastTooltips,
        start,
        current: start,
        measurement: resolveLineMeasurementBetween(screencastTooltips, start, start),
        cursorPosition: {
          x: event.clientX,
          y: event.clientY,
        },
      });
    },
    [getImagePointFromClientPosition, isEnabled, onStart, screencastTooltips],
  );

  const handlePointerMove: PointerEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (!activeLineMeasurement || activeLineMeasurement.pointerId !== event.pointerId) {
        return;
      }

      preventLineMeasurementEvent(event);

      const current = getImagePointFromClientPosition(event.currentTarget, event.clientX, event.clientY);
      setLineMeasurement((prevLineMeasurement) => {
        if (!prevLineMeasurement || prevLineMeasurement.pointerId !== event.pointerId) {
          return prevLineMeasurement;
        }

        return {
          ...prevLineMeasurement,
          current: current ?? prevLineMeasurement.current,
          measurement:
            current && screencastTooltips
              ? resolveLineMeasurementBetween(screencastTooltips, prevLineMeasurement.start, current)
              : null,
          cursorPosition: {
            x: event.clientX,
            y: event.clientY,
          },
        };
      });
    },
    [activeLineMeasurement, getImagePointFromClientPosition, screencastTooltips],
  );

  const handlePointerUp: PointerEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      if (!lineMeasurement || lineMeasurement.pointerId !== event.pointerId) {
        return;
      }

      preventLineMeasurementEvent(event);
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      setLineMeasurement(null);
    },
    [lineMeasurement],
  );

  return {
    activeLineMeasurement,
    handleMouseDownCapture,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  };
}

function shouldStartLineMeasurement(event: LineMeasurementStartEvent) {
  return event.button === 0 && event.shiftKey;
}

function preventLineMeasurementEvent(event: PreventableEvent) {
  event.preventDefault();
  event.stopPropagation();
}
